/**
 * Результаты критериев на конкретной записи — то, что показывает группа
 * «Критерии» в панели звонка и зеркалящий её раздел PDF (спека §7, #862).
 *
 * Здесь, а не в маршруте: спека требует ТРИ различимых состояния плюс
 * четвёртое, «не оценено», которого в хранилище нет вовсе — его приходится
 * выводить сопоставлением настроек организации с тем, что на записи лежит.
 * Правило вывода стоит проверять тестом без БД, поэтому оно — чистая функция.
 */
import { NOT_MENTIONED_CODE } from './definition';
import { criterionAppliesTo, type CriterionRecordScope } from './selection';
import type { CallCriterionScope, StoredCallCriterionOption } from './stored';

/**
 * Четыре состояния строки. Список один на схему ответа и на эту сборку:
 * перечисление в api-types выводится отсюда, иначе добавленное состояние
 * прошло бы типы и упало на разборе ответа.
 */
export const RECORD_CRITERION_STATES = [
  'answered',
  'not_found',
  'not_applicable',
  'not_evaluated',
] as const;

export type RecordCriterionState = (typeof RECORD_CRITERION_STATES)[number];

/** Цитата из разговора: номер реплики — то, по чему панель прыгает к моменту. */
export interface RecordCriterionEvidenceItem {
  turn: number;
  quote: string;
  speaker: 'agent' | 'client' | 'unknown';
}

/** Что нужно от определения критерия, чтобы назвать и подписать результат. */
export interface ResultableCriterion {
  id: string;
  name: string;
  resultType: 'flag' | 'score' | 'choice';
  options: StoredCallCriterionOption[] | null;
  scope: CallCriterionScope;
  isActive: boolean;
  archivedAt: string | null;
  /** Момент создания критерия: раньше оценки он был — позже не существовал. */
  createdAt: string;
}

/** Строка сигнала, как её отдаёт `RecordSignalRepository.listCurrentByRecord`. */
export interface EvaluatedCriterionSignal {
  signalId: string;
  applicable: boolean;
  unsure: boolean;
  valueBool: boolean | null;
  valueScore: number | null;
  valueCode: string | null;
  triggered: boolean | null;
  evidence: RecordCriterionEvidenceItem[];
  note: string | null;
  createdAt: Date;
}

export interface RecordCriterionResultItem {
  criterionId: string;
  name: string;
  resultType: 'flag' | 'score' | 'choice';
  state: RecordCriterionState;
  valueBool: boolean | null;
  valueScore: number | null;
  valueCode: string | null;
  valueLabel: string | null;
  triggered: boolean | null;
  evidence: RecordCriterionEvidenceItem[];
  note: string | null;
  evaluatedAt: string | null;
}

/**
 * Как ответ читается человеку. Экспортировано для проверки формулировки в
 * конструкторе (#861): превью обязано показывать тот же вердикт, что панель
 * звонка покажет после сохранения, а не свою трактовку того же ответа.
 */
export function criterionAnswerState(
  signal: Pick<EvaluatedCriterionSignal, 'applicable' | 'unsure' | 'valueCode'>
): RecordCriterionState {
  if (!signal.applicable) return 'not_applicable';
  if (signal.unsure) return 'not_found';
  /**
   * `not_mentioned` — не вариант ответа, а «тема не поднималась».
   *
   * Код зарезервирован: схема варианта его отвергает («код зарезервирован»,
   * `definition.ts`), а хранилище пишет его двумя обычными путями — модель
   * отвечает им сама (так велит промпт и допускает схема ответа) либо ответ
   * понижается до него, когда вариант требовал цитату, а заземлённой цитаты не
   * пришло (`signal.ts`). Строка при этом идёт как обычный ответ
   * (`applicable: true, unsure: false`).
   *
   * Без этой ветки панель и PDF печатали человеку машинную строку
   * `not_mentioned` — ровно то, что комментарий у схемы запрещает: «код —
   * служебное значение, показывать его человеку нельзя» (находка `/code-review`
   * на #1121). И перевода у неё нет ни в одной локали.
   */
  if (signal.valueCode === NOT_MENTIONED_CODE) return 'not_found';
  return 'answered';
}

/**
 * Подпись варианта списка. Ищется и среди архивных: код из вариантов не
 * удаляется (`planCriterionEdit`), поэтому подпись находится всегда — а если
 * строку всё же испортили вручную, лучше показать код, чем пустое место.
 */
function labelOf(criterion: ResultableCriterion, code: string | null): string | null {
  if (code === null) return null;
  const option = criterion.options?.find((o) => o.code === code);
  return option ? option.label : code;
}

/**
 * Собрать строки группы «Критерии» для записи.
 *
 * Порядок — порядок `criteria` (создание критериев); он же в настройках и в
 * промпте, и панель не должна предлагать четвёртый.
 *
 * **«Не оценено» показывается не для каждого пробела.** Критерий, созданный
 * ПОСЛЕ разбора этой записи, на ней никогда и не мог сработать: помечать его
 * «не оценено» значило бы заполнить панели всех прошлых звонков строками, из
 * которых ничего не следует. Момент разбора — самый ПОЗДНИЙ из известных: самая
 * поздняя из оценок записи и `analyzedAt` от вызывающего.
 *
 * **Ноль оценок у разобранной записи — это не «критериев нет».** Если
 * провайдер или разбор ответа упали на ВСЕХ критериях, строк не появится ни
 * одной, и без `analyzedAt` такая запись выглядела бы как организация без
 * критериев — молчание вместо «не оценено» (находка Codex на #1121). Поэтому
 * момент разбора приходит извне: у записи, дошедшей до конца конвейера, он
 * есть всегда. `analyzedAt: null` оставлен для случая, когда момента правда
 * нет, — тогда утверждать «не оценено» не о чем.
 *
 * Тот же момент нужен и когда оценки ЕСТЬ: на записи со СТАРОЙ оценкой
 * следующий прогон мог упасть на критерии, созданном уже после неё, — и,
 * предпочитая оценку записи, функция объявляла бы такой критерий
 * «не существовавшим» вместо «не оценено» (находка Codex, раунд 3).
 *
 * Строка сигнала показывается ВСЕГДА, даже если критерий с тех пор поставили
 * на паузу, вывели из области или заархивировали: результат на этом звонке был
 * получен, и прятать его значило бы переписывать историю.
 */
export function buildRecordCriteriaResults(
  criteria: readonly ResultableCriterion[],
  signals: readonly EvaluatedCriterionSignal[],
  record: CriterionRecordScope,
  options: {
    /** Когда запись разбирали последний раз, если это известно вызывающему. */
    analyzedAt?: Date | null;
  } = {}
): RecordCriterionResultItem[] {
  const bySignalId = new Map(signals.map((s) => [s.signalId, s]));
  const latestSignalAt = signals.reduce<Date | null>(
    (latest, s) => (latest === null || s.createdAt > latest ? s.createdAt : latest),
    null
  );
  /**
   * Самый поздний из известных моментов, а не «оценки важнее записи».
   *
   * Ошибиться можно в обе стороны, и они не равны: лишнее «не оценено» — это
   * проверяемое утверждение, по которому человек перезапустит оценку, а лишнее
   * молчание скрывает критерий целиком.
   */
  const known = [latestSignalAt, options.analyzedAt ?? null].filter(
    (at): at is Date => at !== null
  );
  const evaluatedAt =
    known.length === 0 ? null : new Date(Math.max(...known.map((at) => at.getTime())));

  const results: RecordCriterionResultItem[] = [];
  for (const criterion of criteria) {
    const signal = bySignalId.get(criterion.id);

    if (!signal) {
      if (evaluatedAt === null) continue;
      if (!criterion.isActive || criterion.archivedAt !== null) continue;
      if (!criterionAppliesTo(criterion.scope, record)) continue;
      // Строка ISO из D1 и метка Postgres сравниваются как моменты. Критерий,
      // созданный в те же секунды, что и оценка, может попасть на любую
      // сторону — оба исхода честны, поэтому точность здесь не важна.
      if (new Date(criterion.createdAt) >= evaluatedAt) continue;
      results.push({
        criterionId: criterion.id,
        name: criterion.name,
        resultType: criterion.resultType,
        state: 'not_evaluated',
        valueBool: null,
        valueScore: null,
        valueCode: null,
        valueLabel: null,
        triggered: null,
        evidence: [],
        note: null,
        evaluatedAt: null,
      });
      continue;
    }

    const state = criterionAnswerState(signal);
    // У состояния без ответа значения нет вовсе: `not_mentioned` в колонке —
    // служебная метка «темы не было», и отдавать её как код варианта нельзя.
    const answered = state === 'answered';
    results.push({
      criterionId: criterion.id,
      name: criterion.name,
      resultType: criterion.resultType,
      state,
      valueBool: answered ? signal.valueBool : null,
      valueScore: answered ? signal.valueScore : null,
      valueCode: answered ? signal.valueCode : null,
      valueLabel: answered ? labelOf(criterion, signal.valueCode) : null,
      triggered: answered ? signal.triggered : null,
      evidence: signal.evidence,
      note: signal.note,
      evaluatedAt: signal.createdAt.toISOString(),
    });
  }
  return results;
}
